import "../styles/eventModes.css";
import type { BossTierView, EventModes, OdysseyView, RaceView } from "@highmarch/app";
import { BalanceData, EventReward, LiveEventData, MEDALS, Medal, ModePrize, eventEndsAt } from "@highmarch/domain";
import { btnHtml } from "../kit";
import { icon } from "../iconsKit";
import { PaintedIcon, paintedIconHtml } from "../featureIcons";
import { portraitUrl } from "../portraits";
import { checkHtml, lockChipHtml, timerChipHtml, wellHtml } from "../components";
import { emberGlyph, rewardArt, rewardWordsHtml, trophyTagHtml } from "./eventRewards";
import { fmtLeft } from "./eventsSheet";

/**
 * The Events sheet's panels for the weekly event modes (MP-21, docs/MODES.md §5), under the week's banner and track:
 * Boss Week's tier ladder (5 tiers and Elite, each prize in its well, locked tiers with a lock chip), Race Week's road
 * (the week's best time, the three medals with their times and states, the road's sketch) and the Odyssey's three maps
 * (progress, the life pool, attempts left, the deck as tower portraits). One gold button per panel: the run to play
 * next. Presentation only: what a run is and pays comes from @highmarch/app eventModes.ts.
 */

/** A prize's well (CSS px of its art) and its embers. */
const WELL_ART_PX = 30, EMBER_PX = 13, MEDAL_PX = 40, DECK_ART_PX = 34, BOSS_ART_PX = 46;
const MEDAL_ICON: Readonly<Record<Medal, PaintedIcon>> = { bronze: "achievementBronze", silver: "achievementSilver", gold: "achievementGold" };
const MEDAL_NAME: Readonly<Record<Medal, string>> = { bronze: "Bronze", silver: "Silver", gold: "Gold" };
const PERCENT = 100;

/** "4:52.3" from sim milliseconds (tenths, floored). */
export function fmtRaceTime(ms: number): string {
  const tenths = Math.floor(ms / 100), m = Math.floor(tenths / 600), s = Math.floor((tenths % 600) / 10);
  return `${m}:${String(s).padStart(2, "0")}.${tenths % 10}`;
}
/** "4:50": a medal's time. */
const fmtLimit = (ms: number): string => { const s = Math.floor(ms / 1000); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };

function prizeHtml(r: EventReward, embers: number, dim = false): string {
  // MP-23: the prize's Trophies under its well, beside its embers
  const tro = (r.trophies ?? 0) > 0 ? trophyTagHtml(r.trophies ?? 0, EMBER_PX, true) : "";
  return `<span class="mprize">${wellHtml({ art: rewardArt(r, WELL_ART_PX), rarity: r.chest, size: "sm", dim })}${embers > 0 || tro ? `<span class="mpays">${embers > 0 ? `<b class="emb">${emberGlyph(EMBER_PX)}+${embers}</b>` : ""}${tro}</span>` : ""}</span>`;
}

function headHtml(badge: string, e: LiveEventData, now: number): string {
  return `<div class="evhead"><span class="badge disp">${badge}</span>${timerChipHtml(`${fmtLeft(eventEndsAt(e) - now)} left`, { inline: true })}</div>`;
}

// ------------------------------------------------------------------ Boss Week

function tierFacts(v: BossTierView): string {
  const d = v.fight.data, parts = [`Health ×${d.hpMult.toNumber().toFixed(1)}`];
  if (!d.armour.isZero()) parts.push(`+${Math.round(d.armour.toNumber() * PERCENT)}% armour`);
  if (d.countMult.toNumber() > 1) parts.push(`+${Math.round((d.countMult.toNumber() - 1) * PERCENT)}% raiders`);
  if (v.fight.twist) parts.push(v.fight.twist.name);
  return parts.join(" · ");
}

export function bossPanelHtml(B: BalanceData, modes: EventModes, e: LiveEventData, now: number): string {
  const tiers = modes.bossTiers(now);
  if (tiers.length === 0) return "";
  const first = tiers[0].fight, boss = B.enemies[B.level(first.level).bossEnemyIndex];
  const next = tiers.find((t) => t.open && !t.cleared);
  const rows = tiers.map((t) => {
    const elite = t.fight.data.elite, label = elite ? "Elite" : `Tier ${t.tier}`;
    const state = t.cleared ? "cleared" : t.open ? "open" : "is-locked";
    const end = t.cleared ? checkHtml() : t === next ? btnHtml("FIGHT", "gold", { icon: "sword", className: "disp bossgo", data: `data-tier="${t.tier}"` })
      : lockChipHtml(`Tier ${t.tier - 1}`);
    return `<li class="mtier ${state}${elite ? " elite" : ""}"><i class="tn disp">${elite ? icon("skull", 16) : t.tier}</i><span class="tl"><b class="disp">${label}</b><small>${tierFacts(t)}</small></span>${prizeHtml(t.fight.data.reward, t.fight.data.embers, !t.open && !t.cleared)}<span class="te">${end}</span></li>`;
  }).join("");
  const art = portraitUrl(boss.id);
  const done = tiers.every((t) => t.cleared);
  return `<section class="evcard evmode boss">
    ${headHtml("BOSS WEEK", e, now)}
    <div class="evbody"><div class="disc portrait">${art ? `<img decoding="sync" src="${art}" width="${BOSS_ART_PX}" height="${BOSS_ART_PX}" alt="">` : icon("skull", 26)}</div>
      <div class="txt"><b>${boss.name} · Level ${first.level}</b><small>${B.events.modes!.boss.stages} waves on ${B.level(first.level).name}, the ${boss.name} last. Each tier is the same fight, tougher.</small></div></div>
    <ol class="mladder">${rows}</ol>
    ${done ? `<p class="mdone">${icon("trophy", 15)} Every tier beaten this week, Elite too. A new boss on its next week.</p>` : ""}
  </section>`;
}

// ------------------------------------------------------------------ Race Week

function medalHtml(r: RaceView, m: Medal, prize: ModePrize): string {
  const limit = m === "gold" ? r.road.road.goldMs : m === "silver" ? r.road.road.silverMs : r.road.road.bronzeMs;
  const paid = r.paid.includes(m);
  return `<div class="mmedal ${m}${paid ? " won" : ""}">${wellHtml({ art: paintedIconHtml(MEDAL_ICON[m], MEDAL_PX), size: "md" })}
    <b class="disp">${MEDAL_NAME[m]}</b><small>${icon("clock", 12)} ${fmtLimit(limit)}</small>
    <span class="mpay">${paid ? `${checkHtml()} Won` : `<span class="rw">${rewardWordsHtml(prize.reward)}</span><span class="emb">${emberGlyph(EMBER_PX)}+${prize.embers}</span>`}</span></div>`;
}

export function racePanelHtml(B: BalanceData, modes: EventModes, e: LiveEventData, now: number): string {
  const r = modes.race(now);
  if (!r) return "";
  const medals = B.events.modes!.race.medals;
  const runs = `${r.runs} run${r.runs === 1 ? "" : "s"}`;
  const best = r.best > 0 ? `<span class="mbest">Your best <b class="disp">${fmtRaceTime(r.best)}</b>${r.medal ? paintedIconHtml(MEDAL_ICON[r.medal], 22) : ""}<small>· ${runs}</small></span>` : `<span class="mbest none">No time yet this week</span>`;
  const allGold = r.paid.includes("gold");
  return `<section class="evcard evmode race">
    ${headHtml("RACE WEEK", e, now)}
    <div class="evbody"><div class="mroad" aria-hidden="true"></div>
      <div class="txt"><b>Level ${r.road.level} · ${B.level(r.road.level).name}</b><small>${r.road.road.stages} waves · the same road and cards every try this week. The clock stops while you pick a card.</small>${best}</div></div>
    <div class="mmedals">${[...MEDALS].reverse().map((m) => medalHtml(r, m, medals[m])).join("")}</div>
    ${btnHtml(allGold ? "RACE AGAIN" : r.runs > 0 ? "RACE AGAIN" : "RACE", allGold ? "dark" : "gold", { icon: "fast", className: "disp go racego" })}
  </section>`;
}

// ------------------------------------------------------------------ Odyssey

function deckHtml(B: BalanceData, deck: readonly number[]): string {
  return deck.map((i) => { const t = B.towers[i], url = portraitUrl(t.id, 2); return wellHtml({ art: url ? `<img decoding="sync" src="${url}" width="${DECK_ART_PX}" height="${DECK_ART_PX}" alt="${t.name}">` : icon("tower", 22), size: "sm", label: t.name }); }).join("");
}

function odysseyCta(v: OdysseyView): string {
  if (!v.next) return btnHtml("NO ATTEMPTS LEFT", "dark", { icon: "lock", className: "disp go", disabled: true });
  const label = v.next.newAttempt ? (v.attemptsLeft === v.attempts ? "START ODYSSEY" : "NEW ATTEMPT") : v.inProgress ? `CONTINUE MAP ${v.next.map}` : `PLAY MAP ${v.next.map}`;
  return btnHtml(label, "gold", { icon: "play", className: "disp go odygo" });
}

export function odysseyPanelHtml(B: BalanceData, modes: EventModes, e: LiveEventData, now: number): string {
  const v = modes.odyssey(now);
  if (!v) return "";
  const O = B.events.modes!.odyssey, at = v.next && !v.next.newAttempt ? v.next.map : 0;
  const maps = v.levels.map((l, i) => {
    const n = i + 1, cleared = v.cleared.includes(n), here = n === at;
    const state = here ? "here" : cleared ? "cleared" : "ahead";
    return `<div class="mmap ${state}"><i class="tn disp">${n}</i><b class="disp">${B.level(l).name}</b><small>Level ${l} · ${O.stages} waves</small>${prizeHtml(O.maps[i].reward, O.maps[i].embers, !cleared && !here)}${cleared ? `<span class="mst">${checkHtml()}</span>` : here ? `<span class="mst now">NEXT</span>` : ""}</div>`;
  }).join("");
  const livesPct = Math.round((PERCENT * v.lives) / Math.max(1, v.maxLives));
  return `<section class="evcard evmode odyssey">
    ${headHtml("ODYSSEY", e, now)}
    <div class="mstats"><span class="mchip lives">${icon("heart", 15)}<b class="disp">${v.lives}</b>/${v.maxLives} lives<i class="mbar"><i style="width:${livesPct}%"></i></i></span><span class="mchip">${icon("flag", 15)}<b class="disp">${v.attemptsLeft}</b> of ${v.attempts} attempts left</span></div>
    <div class="mmaps">${maps}</div>
    <div class="mdeck"><small class="disp">YOUR DECK</small><div class="wells">${deckHtml(B, v.deck)}</div></div>
    <div class="mfull${v.full ? " won" : ""}"><span class="tl"><b class="disp">All three in one attempt</b><small>${v.full ? "Done this week!" : rewardWordsHtml(O.full.reward)}</small></span>${v.full ? checkHtml() : prizeHtml(O.full.reward, O.full.embers)}</div>
    ${odysseyCta(v)}
  </section>`;
}

/** The week's panel for the live event (none for a week without a mode). */
export function eventModePanelHtml(B: BalanceData, modes: EventModes, e: LiveEventData, now: number): string {
  const w = modes.week(now);
  if (!w || w.event.id !== e.id) return "";
  return w.mode === "boss" ? bossPanelHtml(B, modes, e, now) : w.mode === "race" ? racePanelHtml(B, modes, e, now) : odysseyPanelHtml(B, modes, e, now);
}
